import { useEffect, useState } from 'react'
import { faCircleCheck } from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import Icon from '../../components/icon/Icon'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  acknowledgeAnnouncement,
  getMyAnnouncements,
} from '../../_data/resident'
import { RenterBulletin } from '../../_types/resident'
import { formatLedgerDate } from '../../_utilities/resident-format'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  AnnouncementList,
  AnnouncementHeaderRow,
  AnnouncementTitle,
  AnnouncementDate,
  AnnouncementBody,
  AcknowledgeRow,
  AcknowledgeRequiredTag,
  AcknowledgeButton,
  AcknowledgedNote,
} from './styled'

/**
 * The renter's Announcements feed for the selected residence: the property
 * manager's published broadcasts (newest first), read-only. Keyed on the
 * selected tenancy so switching residence reloads the right feed.
 *
 * @returns {JSX.Element}
 */
const AnnouncementsPage = (): JSX.Element => {
  const { selectedTenancy, selectedTenancyId, isLoadingTenancies } =
    useResidentTenancy()
  const [announcements, setAnnouncements] = useState<RenterBulletin[]>([])
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] =
    useState<boolean>(false)
  const [acknowledgingId, setAcknowledgingId] = useState<string>('')

  useEffect(() => {
    if (selectedTenancyId === '') {
      setAnnouncements([])
      setIsLoadingAnnouncements(false)
      return
    }

    let isMounted = true
    setAnnouncements([])
    setIsLoadingAnnouncements(true)

    void getMyAnnouncements(selectedTenancyId).then(loaded => {
      if (isMounted === false) {
        return
      }
      setAnnouncements(loaded)
      setIsLoadingAnnouncements(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  const handleAcknowledge = (bulletinId: string): void => {
    if (selectedTenancyId === '') {
      return
    }
    setAcknowledgingId(bulletinId)
    void acknowledgeAnnouncement(selectedTenancyId, bulletinId).then(
      acknowledgedAt => {
        setAcknowledgingId('')
        if (acknowledgedAt === null) {
          return
        }
        setAnnouncements(previous =>
          previous.map(announcement =>
            announcement.id === bulletinId
              ? { ...announcement, acknowledgedAt }
              : announcement,
          ),
        )
      },
    )
  }

  const hasAnnouncements = announcements.length > 0

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <PageHeader>
          <PageTitle>Announcements</PageTitle>
          <PageSubtitle>
            Updates from your property manager, newest first.
          </PageSubtitle>
        </PageHeader>

        <TenancySwitcher />

        {isLoadingTenancies === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading…</QuietText>
          </CardSurface>
        ) : null}

        {isLoadingTenancies === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have any tenancies yet. When your property manager
              invites you, claim the invite from your email to see your
              announcements here.
            </QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null && isLoadingAnnouncements === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading announcements…</QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null &&
        isLoadingAnnouncements === false &&
        hasAnnouncements === false ? (
          <CardSurface padding="loose">
            <QuietText>
              No announcements yet. When your property manager posts one, it
              shows up here.
            </QuietText>
          </CardSurface>
        ) : null}

        {hasAnnouncements === true ? (
          <AnnouncementList>
            {announcements.map(announcement => (
              <CardSurface key={announcement.id} padding="standard">
                <AnnouncementHeaderRow>
                  <AnnouncementTitle>{announcement.title}</AnnouncementTitle>
                  {announcement.publishedAt !== null ? (
                    <AnnouncementDate>
                      {formatLedgerDate(announcement.publishedAt)}
                    </AnnouncementDate>
                  ) : null}
                </AnnouncementHeaderRow>
                <AnnouncementBody>{announcement.body}</AnnouncementBody>
                {announcement.requiresAcknowledgement === true &&
                announcement.acknowledgedAt !== null ? (
                  <AcknowledgeRow>
                    <AcknowledgedNote>
                      <Icon icon={faCircleCheck} />
                      Acknowledged on{' '}
                      {formatLedgerDate(announcement.acknowledgedAt)}
                    </AcknowledgedNote>
                  </AcknowledgeRow>
                ) : null}
                {announcement.requiresAcknowledgement === true &&
                announcement.acknowledgedAt === null ? (
                  <AcknowledgeRow>
                    <AcknowledgeRequiredTag>
                      Acknowledgement required
                    </AcknowledgeRequiredTag>
                    <AcknowledgeButton
                      type="button"
                      disabled={acknowledgingId === announcement.id}
                      onClick={() => handleAcknowledge(announcement.id)}
                    >
                      {acknowledgingId === announcement.id
                        ? 'Saving…'
                        : 'I’ve read this'}
                    </AcknowledgeButton>
                  </AcknowledgeRow>
                ) : null}
              </CardSurface>
            ))}
          </AnnouncementList>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default AnnouncementsPage
